import type { RecapKind, RecapRow } from '@busy/api';
import type { RecapData } from '@busy/core';
import { useT } from '../../lib/i18n';
import { useUI } from '../../store/ui';

// Print-only layout for the weekly / monthly recap. Rendered into a
// portal at the body level by RecapModal when the user taps "Share
// PDF". The .recap-print-root host is hidden in normal screen
// rendering — only the @media print rules in main.css promote it
// visible inside the print dialog, so the resulting PDF contains JUST
// this layout (no app chrome, no modal overlay).
//
// Visual goal: read like a polished printout of the on-phone recap —
// same hierarchy, same data, but laid out for a Letter/A4 page (top
// hero band, KPI tiles, list sections, footer with generation stamp).

export function RecapPrintLayout({
  recap,
  barberFirstName,
}: {
  recap: RecapRow;
  /** First name of the user — used in the hero "for {name}" line. Optional;
   *  hidden when not provided (e.g. user hasn't set a first name yet). */
  barberFirstName: string | null;
}) {
  const t = useT();
  const language = useUI((s) => s.language);
  const locale = language === 'es' ? 'es-US' : 'en-US';
  const data = recap.data as unknown as RecapData;
  const periodLabel = formatPeriod(recap.period_start, recap.period_end, recap.kind, locale);
  const revDollars = Math.round((data.totals?.revenue_cents ?? 0) / 100);
  const generatedAt = new Date().toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const kindLabel =
    recap.kind === 'weekly'
      ? t('recap.eyebrow.weekly')
      : recap.kind === 'monthly'
        ? t('recap.eyebrow.monthly')
        : recap.kind === 'quarterly'
          ? t('recap.eyebrow.quarterly')
          : t('recap.eyebrow.annual');

  // Secondary stats — operational rates / engagement signals. Dollar
  // totals for tips live in the Tax Summary block, so this list now
  // surfaces RATES (tip %, attach %) + non-dollar context (streak,
  // chair time, lessons).
  const secondary: Array<{ label: string; value: string; tone?: 'tip' | 'highlight' }> = [];
  const streakInPeriod =
    data.streak?.longest_in_period ?? data.streak?.current ?? 0;
  if (streakInPeriod > 0) {
    secondary.push({
      label: t('recap.row.streakInPeriod'),
      value: t(
        streakInPeriod === 1 ? 'recap.row.streak.days_one' : 'recap.row.streak.days_other',
        { n: streakInPeriod },
      ),
      tone: 'highlight',
    });
  }
  if ((data.totals?.new_clients ?? 0) > 0) {
    secondary.push({ label: t('recap.row.newClients'), value: String(data.totals.new_clients) });
  }
  if ((data.totals?.avg_ticket_cents ?? 0) > 0) {
    secondary.push({
      label: t('recap.row.avgTicket'),
      value: `$${Math.round(data.totals.avg_ticket_cents / 100)}`,
    });
  }
  if ((data.totals?.tip_rate_pct ?? null) != null) {
    secondary.push({
      label: t('recap.row.tipRate'),
      value: `${data.totals?.tip_rate_pct ?? 0}%`,
      tone: 'tip',
    });
  }
  if ((data.totals?.tip_attach_rate_pct ?? null) != null) {
    secondary.push({
      label: t('recap.row.tipAttachRate'),
      value: `${data.totals?.tip_attach_rate_pct ?? 0}%`,
      tone: 'tip',
    });
  }
  if ((data.totals?.chair_time_min ?? 0) > 0) {
    const h = Math.floor(data.totals.chair_time_min / 60);
    const m = data.totals.chair_time_min % 60;
    secondary.push({
      label: t('recap.row.chairTime'),
      value: h > 0 ? `${h}h ${m}m` : `${m}m`,
    });
  }
  if ((data.totals?.addon_attach_count ?? 0) > 0 && (data.totals?.cuts ?? 0) > 0) {
    const pct = Math.round(((data.totals.addon_attach_count ?? 0) / (data.totals.cuts ?? 1)) * 100);
    secondary.push({ label: t('recap.row.addonAttach'), value: `${pct}%` });
  }
  if ((data.lessons?.completed ?? 0) > 0) {
    secondary.push({ label: t('recap.row.lessons'), value: String(data.lessons.completed) });
  }

  return (
    <div className="recap-print-root">
      <div
        style={{
          fontFamily:
            "Inter, system-ui, -apple-system, 'Segoe UI', sans-serif",
          color: '#0c1530',
          padding: '0',
          margin: '0',
          width: '100%',
          maxWidth: '720px',
          marginLeft: 'auto',
          marginRight: 'auto',
        }}
      >
        {/* ─── HERO ────────────────────────────────────────────────────── */}
        <div
          className="recap-print-section"
          style={{
            background: 'linear-gradient(135deg, #0c1530 0%, #131e3f 100%)',
            color: 'white',
            padding: '32px 32px 28px',
            borderRadius: '12px',
            marginBottom: '20px',
          }}
        >
          <div
            style={{
              fontSize: '11px',
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              fontWeight: 800,
              color: '#e8c66a',
              marginBottom: '8px',
            }}
          >
            {kindLabel}
          </div>
          <h1
            style={{
              fontFamily: "Anton, Impact, sans-serif",
              fontSize: '38px',
              letterSpacing: '0.02em',
              lineHeight: 1.1,
              margin: 0,
            }}
          >
            {periodLabel}
          </h1>
          {barberFirstName && (
            <div
              style={{
                marginTop: '10px',
                fontSize: '13px',
                color: 'rgba(255,255,255,0.75)',
              }}
            >
              {t('recap.print.forBarber', { name: barberFirstName })}
            </div>
          )}
          <div
            style={{
              marginTop: '4px',
              fontSize: '12px',
              color: 'rgba(255,255,255,0.6)',
            }}
          >
            {t('recap.subhead', {
              kind:
                recap.kind === 'weekly'
                  ? t('recap.kind.week')
                  : recap.kind === 'monthly'
                    ? t('recap.kind.month')
                    : recap.kind === 'quarterly'
                      ? t('recap.kind.quarter')
                      : t('recap.kind.year'),
            })}
          </div>
        </div>

        {/* ─── TOTAL INCOME HERO ─────────────────────────────────────── */}
        {/* Tax-prep headline. Cuts + tips combined. Anchors the page so
            a tax preparer reads the big number first and can skim down
            to the breakdown rows below. Falls back to revenue_cents
            for v1 recap rows that don't have total_income_cents. */}
        <PrintTotalIncomeHero data={data} t={t} />

        {/* ─── HEADLINE KPIs ─────────────────────────────────────────── */}
        <div
          className="recap-print-section"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '12px',
            marginBottom: '20px',
          }}
        >
          <PrintKpi
            label={t('recap.kpi.revenue')}
            value={`$${revDollars.toLocaleString()}`}
            change={data.vs_prior?.revenue_change_pct ?? null}
            flatLabel={t('recap.flat')}
          />
          <PrintKpi
            label={t('recap.kpi.cuts')}
            value={String(data.totals?.cuts ?? 0)}
            change={data.vs_prior?.cuts_change_pct ?? null}
            flatLabel={t('recap.flat')}
          />
          <PrintKpi
            label={t('recap.kpi.tips')}
            value={`$${Math.round((data.totals?.tips_cents ?? 0) / 100).toLocaleString()}`}
            change={data.vs_prior?.tips_change_pct ?? null}
            flatLabel={t('recap.flat')}
            valueColor="#16a34a"
          />
        </div>

        {/* ─── TAX SUMMARY ───────────────────────────────────────────── */}
        <PrintTaxSummary data={data} t={t} />

        {/* ─── PAYMENT METHOD BREAKDOWN ──────────────────────────────── */}
        {/* 1099-K reconciliation workbook. Each processor reports
            independently to the IRS, so this table lets the barber
            (or their tax preparer) match app-tracked totals against
            each form they receive at year-end. */}
        <PrintPaymentMethodBreakdown data={data} t={t} />

        {/* ─── SUB-PERIOD BREAKDOWN ──────────────────────────────────── */}
        <PrintSubPeriodBreakdown data={data} t={t} />

        {/* ─── SECONDARY STATS ───────────────────────────────────────── */}
        {secondary.length > 0 && (
          <PrintSection
            title={t('recap.print.secondaryTitle')}
            divider={false}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '6px 24px',
              }}
            >
              {secondary.map((r) => (
                <div
                  key={r.label}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: '12px',
                    padding: '6px 0',
                    borderBottom: '1px solid rgba(12,21,48,0.08)',
                    fontSize: '13px',
                  }}
                >
                  <span style={{ color: '#5b6478' }}>{r.label}</span>
                  <strong
                    style={{
                      fontWeight: 700,
                      fontVariantNumeric: 'tabular-nums',
                      color:
                        r.tone === 'tip'
                          ? '#16a34a'
                          : r.tone === 'highlight'
                            ? '#0c1530'
                            : undefined,
                    }}
                  >
                    {r.value}
                  </strong>
                </div>
              ))}
            </div>
          </PrintSection>
        )}

        {/* ─── TOP CLIENTS ───────────────────────────────────────────── */}
        {data.top_clients?.length > 0 && (
          <PrintSection title={t('recap.topClients')}>
            <PrintList
              items={data.top_clients.map((c) => ({
                label: c.name,
                meta: `${c.visits}× · $${Math.round(c.revenue_cents / 100)}`,
              }))}
            />
          </PrintSection>
        )}

        {/* ─── TOP CUT TYPES ─────────────────────────────────────────── */}
        {data.top_cut_types?.length > 0 && (
          <PrintSection title={t('recap.topCuts')}>
            <PrintList
              items={data.top_cut_types.map((c) => ({
                label: c.name,
                meta: `×${c.count} · $${Math.round(c.revenue_cents / 100)}`,
              }))}
            />
          </PrintSection>
        )}

        {/* ─── TOP ADDONS ────────────────────────────────────────────── */}
        {data.top_addons?.length > 0 && (
          <PrintSection title={t('recap.topAddons')}>
            <PrintList
              items={data.top_addons.map((a) => ({
                label: a.name,
                meta: `×${a.count} · $${Math.round(a.revenue_cents / 100)}`,
              }))}
            />
          </PrintSection>
        )}

        {/* ─── MILESTONES ────────────────────────────────────────────── */}
        {data.milestones?.length > 0 && (
          <PrintSection
            title={t('recap.milestones', { count: data.milestones.length })}
          >
            <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {data.milestones.map((m, i) => (
                <li
                  key={`${m.kind}-${m.threshold}-${i}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '6px 0',
                    fontSize: '13px',
                  }}
                >
                  <span
                    style={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      background: '#d4a942',
                      display: 'inline-block',
                      flexShrink: 0,
                    }}
                  />
                  <span>{t(`recap.milestone.${m.kind}`, { n: m.threshold })}</span>
                </li>
              ))}
            </ul>
          </PrintSection>
        )}

        {/* ─── BEST DAY ──────────────────────────────────────────────── */}
        {data.best_day && data.best_day.revenue_cents > 0 && (
          <PrintSection title={t('recap.bestDay')}>
            <p style={{ margin: 0, fontSize: '14px', color: '#3a4561' }}>
              {new Date(data.best_day.date + 'T00:00:00').toLocaleDateString(locale, {
                weekday: 'long',
                month: 'short',
                day: 'numeric',
              })}{' '}
              ·{' '}
              <strong>
                ${Math.round(data.best_day.revenue_cents / 100).toLocaleString()}
              </strong>
            </p>
          </PrintSection>
        )}

        {/* ─── FOOTER ────────────────────────────────────────────────── */}
        <div
          style={{
            marginTop: '32px',
            paddingTop: '16px',
            borderTop: '1px solid rgba(12,21,48,0.12)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            fontSize: '11px',
            color: '#5b6478',
          }}
        >
          <span
            style={{
              fontFamily: "Anton, Impact, sans-serif",
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              fontSize: '14px',
              color: '#0c1530',
            }}
          >
            {t('recap.print.brand')}
          </span>
          <span>{t('recap.print.generatedOn', { date: generatedAt })}</span>
        </div>
      </div>
    </div>
  );
}

function PrintKpi({
  label,
  value,
  change,
  flatLabel,
  valueColor,
}: {
  label: string;
  value: string;
  change: number | null;
  flatLabel: string;
  /** Override the value color (used by the Tips KPI to render green). */
  valueColor?: string;
}) {
  let badge: { text: string; bg: string; fg: string } | null = null;
  if (change != null) {
    if (change > 0) badge = { text: `+${change}%`, bg: '#e3f1ea', fg: '#157a47' };
    else if (change < 0) badge = { text: `${change}%`, bg: '#fdeaee', fg: '#9c0c23' };
    else badge = { text: flatLabel, bg: '#f7f5ee', fg: '#5b6478' };
  }
  return (
    <div
      style={{
        background: '#f7f5ee',
        padding: '14px 10px',
        borderRadius: '10px',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          fontFamily: "Anton, Impact, sans-serif",
          fontSize: '28px',
          lineHeight: 1,
          color: valueColor ?? '#0c1530',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: '10px',
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          fontWeight: 700,
          color: '#5b6478',
          marginTop: '8px',
        }}
      >
        {label}
      </div>
      {badge && (
        <div
          style={{
            display: 'inline-block',
            fontSize: '10px',
            fontWeight: 800,
            padding: '2px 6px',
            borderRadius: '4px',
            marginTop: '6px',
            background: badge.bg,
            color: badge.fg,
          }}
        >
          {badge.text}
        </div>
      )}
    </div>
  );
}

function PrintSection({
  title,
  children,
  divider = true,
}: {
  title: string;
  children: React.ReactNode;
  divider?: boolean;
}) {
  return (
    <section
      className="recap-print-section"
      style={{
        marginBottom: '20px',
        paddingTop: divider ? '4px' : 0,
      }}
    >
      <h3
        style={{
          fontSize: '11px',
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          fontWeight: 800,
          color: '#5b6478',
          margin: '0 0 8px',
        }}
      >
        {title}
      </h3>
      {children}
    </section>
  );
}

function PrintList({ items }: { items: Array<{ label: string; meta: string }> }) {
  return (
    <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
      {items.map((it, i) => (
        <li
          key={i}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: '12px',
            padding: '6px 0',
            borderBottom: '1px solid rgba(12,21,48,0.08)',
            fontSize: '13px',
          }}
        >
          <span
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {it.label}
          </span>
          <span style={{ color: '#5b6478', fontSize: '12px', whiteSpace: 'nowrap' }}>
            {it.meta}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** Tax-prep headline band — gold accent showing total income (cuts +
 *  tips) for the period, with a small breakdown subline. Sits above
 *  the KPI grid on the print layout. */
function PrintTotalIncomeHero({
  data,
  t,
}: {
  data: RecapData;
  t: (key: string, vars?: Record<string, string | number>) => string;
}) {
  const totalIncome =
    data.totals?.total_income_cents ??
    (data.totals?.revenue_cents ?? 0) + (data.totals?.tips_cents ?? 0);
  const revenueDollars = Math.round((data.totals?.revenue_cents ?? 0) / 100);
  const tipsDollars = Math.round((data.totals?.tips_cents ?? 0) / 100);
  return (
    <div
      className="recap-print-section"
      style={{
        background: 'linear-gradient(135deg, rgba(212,169,66,0.15), rgba(212,169,66,0.05))',
        border: '1px solid rgba(212,169,66,0.45)',
        borderRadius: '12px',
        padding: '20px 22px',
        marginBottom: '20px',
      }}
    >
      <div
        style={{
          fontSize: '10px',
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          fontWeight: 800,
          color: '#7c5e1c',
          marginBottom: '4px',
        }}
      >
        {t('recap.hero.totalIncome')}
      </div>
      <div
        style={{
          fontFamily: "Anton, Impact, sans-serif",
          fontSize: '40px',
          lineHeight: 1,
          color: '#0c1530',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        ${(totalIncome / 100).toLocaleString()}
      </div>
      <div style={{ fontSize: '12px', color: '#3a4565', marginTop: '8px' }}>
        {t('recap.hero.breakdown', {
          cuts: revenueDollars.toLocaleString(),
          tips: tipsDollars.toLocaleString(),
        })}
      </div>
    </div>
  );
}

/** Tax summary list — the rows a tax preparer wants front-and-center. */
function PrintTaxSummary({
  data,
  t,
}: {
  data: RecapData;
  t: (key: string, vars?: Record<string, string | number>) => string;
}) {
  const hasV2 =
    data.totals?.total_income_cents != null ||
    data.totals?.service_revenue_cents != null ||
    data.totals?.days_worked != null;
  if (!hasV2) return null;
  const rows: Array<{ label: string; value: string; tone?: 'tip' | 'discount' }> = [];
  const totalIncome = data.totals?.total_income_cents ??
    (data.totals?.revenue_cents ?? 0) + (data.totals?.tips_cents ?? 0);
  rows.push({
    label: t('recap.tax.totalIncome'),
    value: `$${Math.round(totalIncome / 100).toLocaleString()}`,
  });
  if ((data.totals?.service_revenue_cents ?? 0) > 0) {
    rows.push({
      label: t('recap.tax.serviceRevenue'),
      value: `$${Math.round((data.totals?.service_revenue_cents ?? 0) / 100).toLocaleString()}`,
    });
  }
  if ((data.totals?.addon_revenue_cents ?? 0) > 0) {
    rows.push({
      label: t('recap.tax.addonRevenue'),
      value: `$${Math.round((data.totals?.addon_revenue_cents ?? 0) / 100).toLocaleString()}`,
    });
  }
  if ((data.totals?.tips_cents ?? 0) > 0) {
    rows.push({
      label: t('recap.tax.tipsTotal'),
      value: `$${Math.round((data.totals?.tips_cents ?? 0) / 100).toLocaleString()}`,
      tone: 'tip',
    });
  }
  if ((data.totals?.distinct_clients ?? 0) > 0) {
    rows.push({
      label: t('recap.tax.distinctClients'),
      value: String(data.totals?.distinct_clients ?? 0),
    });
  }
  if ((data.totals?.days_worked ?? 0) > 0) {
    rows.push({
      label: t('recap.tax.daysWorked'),
      value: String(data.totals?.days_worked ?? 0),
    });
  }
  if ((data.totals?.hours_worked_tenths ?? 0) > 0) {
    rows.push({
      label: t('recap.tax.hoursWorked'),
      value: `${((data.totals?.hours_worked_tenths ?? 0) / 10).toFixed(1)}h`,
    });
  }
  if ((data.totals?.avg_daily_income_cents ?? 0) > 0) {
    rows.push({
      label: t('recap.tax.avgDailyIncome'),
      value: `$${Math.round((data.totals?.avg_daily_income_cents ?? 0) / 100).toLocaleString()}`,
    });
  }
  if ((data.totals?.earning_rate_cents_per_hour ?? null) != null) {
    rows.push({
      label: t('recap.tax.earningRate'),
      value: `$${Math.round((data.totals?.earning_rate_cents_per_hour ?? 0) / 100).toLocaleString()}/hr`,
    });
  }
  const minT = data.totals?.min_ticket_cents ?? 0;
  const maxT = data.totals?.max_ticket_cents ?? 0;
  if (maxT > 0 && minT !== maxT) {
    rows.push({
      label: t('recap.tax.ticketRange'),
      value: `$${Math.round(minT / 100)} – $${Math.round(maxT / 100)}`,
    });
  }
  if ((data.totals?.discount_given_cents ?? 0) > 0) {
    rows.push({
      label: t('recap.tax.discountGiven'),
      value: `$${Math.round((data.totals?.discount_given_cents ?? 0) / 100).toLocaleString()}`,
      tone: 'discount',
    });
  }
  const firstAt = data.period_activity?.first_visit_at;
  const lastAt = data.period_activity?.last_visit_at;
  let activityLine: string | null = null;
  if (firstAt && lastAt) {
    const f = new Date(firstAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const l = new Date(lastAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    activityLine = f === l ? f : `${f} – ${l}`;
  }
  if (activityLine) {
    rows.push({ label: t('recap.tax.activityWindow'), value: activityLine });
  }
  return (
    <PrintSection title={t('recap.tax.heading')}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '6px 24px',
        }}
      >
        {rows.map((r) => (
          <div
            key={r.label}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '12px',
              padding: '6px 0',
              borderBottom: '1px solid rgba(12,21,48,0.08)',
              fontSize: '13px',
            }}
          >
            <span style={{ color: '#5b6478' }}>{r.label}</span>
            <strong
              style={{
                fontWeight: 700,
                fontVariantNumeric: 'tabular-nums',
                color:
                  r.tone === 'tip'
                    ? '#16a34a'
                    : r.tone === 'discount'
                      ? '#b91c1c'
                      : undefined,
              }}
            >
              {r.value}
            </strong>
          </div>
        ))}
      </div>
      <p style={{ fontSize: '10px', color: '#5b6478', marginTop: '10px', lineHeight: 1.4 }}>
        {t('recap.tax.footnote')}
      </p>
    </PrintSection>
  );
}

/** Sub-period rollup — monthly recap: weekly rows. Quarterly +
 *  annual: monthly rows (annual groups by quarter for tax-prep
 *  readability). Mirrors the on-screen modal block. */
/** Per-payment-method breakdown for the print PDF — 1099-K
 *  reconciliation workbook. Lists each active method with cuts,
 *  income, and share %. Hides entirely when only "unspecified"
 *  activity exists (typical pre-migration-0071 recaps). */
function PrintPaymentMethodBreakdown({
  data,
  t,
}: {
  data: RecapData;
  t: (key: string, vars?: Record<string, string | number>) => string;
}) {
  const rows = data.payment_method_breakdown;
  if (!rows || rows.length === 0) return null;
  const active = rows.filter((r) => r.cuts > 0);
  if (active.length === 0) return null;
  if (active.length === 1 && active[0]?.method === 'unspecified') return null;
  return (
    <PrintSection title={t('recap.payment.heading')}>
      <div
        style={{
          border: '1px solid rgba(12,21,48,0.12)',
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      >
        {active.map((r) => {
          const income = Math.round(r.total_income_cents / 100);
          const pct = r.share_pct ?? 0;
          return (
            <div
              key={r.method}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '12px',
                padding: '8px 12px',
                borderTop: '1px solid rgba(12,21,48,0.08)',
                fontSize: '13px',
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, color: '#0c1530', lineHeight: 1.2 }}>
                  {t(`payment.${r.method}`)}
                </div>
                <div style={{ fontSize: '11px', color: '#5b6478' }}>
                  {t('recap.payment.cutsLine', { cuts: r.cuts })}
                </div>
              </div>
              <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                <div style={{ fontWeight: 700, color: '#0c1530' }}>
                  ${income.toLocaleString()}
                </div>
                <div
                  style={{
                    fontSize: '10px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    color: '#5b6478',
                  }}
                >
                  {pct}%
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <p style={{ fontSize: '10px', color: '#5b6478', marginTop: '10px', lineHeight: 1.4 }}>
        {t('recap.payment.footnote')}
      </p>
    </PrintSection>
  );
}

function PrintSubPeriodBreakdown({
  data,
  t,
}: {
  data: RecapData;
  t: (key: string, vars?: Record<string, string | number>) => string;
}) {
  const rows = data.sub_period_breakdowns;
  if (!rows || rows.length === 0) return null;

  if (data.kind === 'annual') {
    const byQuarter = new Map<number, typeof rows>();
    for (const r of rows) {
      const month = new Date(r.period_start + 'T00:00:00').getMonth();
      const q = Math.floor(month / 3) + 1;
      const arr = byQuarter.get(q) ?? [];
      arr.push(r);
      byQuarter.set(q, arr);
    }
    return (
      <PrintSection title={t('recap.breakdown.headingMonthly')}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {[1, 2, 3, 4].map((q) => {
            const months = byQuarter.get(q);
            if (!months || months.length === 0) return null;
            const qTotal = months.reduce((s, m) => s + m.total_income_cents, 0);
            return (
              <div
                key={q}
                style={{
                  border: '1px solid rgba(12,21,48,0.12)',
                  borderRadius: '8px',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    background: '#f7f5ee',
                    padding: '6px 12px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '11px',
                    fontWeight: 800,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    color: '#0c1530',
                  }}
                >
                  <span>{t('recap.breakdown.quarterLabel', { q })}</span>
                  <span style={{ fontVariantNumeric: 'tabular-nums', color: '#3a4565' }}>
                    ${Math.round(qTotal / 100).toLocaleString()}
                  </span>
                </div>
                {months.map((m) => (
                  <PrintBreakdownRow key={m.period_start} row={m} t={t} />
                ))}
              </div>
            );
          })}
        </div>
      </PrintSection>
    );
  }

  const heading =
    data.kind === 'monthly'
      ? t('recap.breakdown.headingWeekly')
      : t('recap.breakdown.headingMonthly');
  return (
    <PrintSection title={heading}>
      <div
        style={{
          border: '1px solid rgba(12,21,48,0.12)',
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      >
        {rows.map((r) => (
          <PrintBreakdownRow key={r.period_start} row={r} t={t} />
        ))}
      </div>
    </PrintSection>
  );
}

function PrintBreakdownRow({
  row,
  t,
}: {
  row: NonNullable<RecapData['sub_period_breakdowns']>[number];
  t: (key: string, vars?: Record<string, string | number>) => string;
}) {
  const income = Math.round(row.total_income_cents / 100);
  const tips = Math.round(row.tips_cents / 100);
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '12px',
        padding: '8px 12px',
        borderTop: '1px solid rgba(12,21,48,0.08)',
        fontSize: '13px',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 700, color: '#0c1530', lineHeight: 1.2 }}>{row.label}</div>
        <div style={{ fontSize: '11px', color: '#5b6478' }}>
          {t('recap.breakdown.metaLine', { cuts: row.cuts, clients: row.distinct_clients })}
        </div>
      </div>
      <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
        <div style={{ fontWeight: 700, color: '#0c1530' }}>${income.toLocaleString()}</div>
        {tips > 0 && (
          <div style={{ fontSize: '10px', color: '#16a34a', fontWeight: 700 }}>
            {t('recap.breakdown.tipsSub', { tips: tips.toLocaleString() })}
          </div>
        )}
      </div>
    </div>
  );
}

function formatPeriod(
  start: string,
  end: string,
  kind: RecapKind,
  locale: string,
): string {
  const startD = new Date(start + 'T00:00:00');
  const endD = new Date(end + 'T00:00:00');
  if (kind === 'annual') {
    return String(startD.getFullYear());
  }
  if (kind === 'quarterly') {
    const q = Math.floor(startD.getMonth() / 3) + 1;
    return `Q${q} ${startD.getFullYear()}`;
  }
  if (kind === 'monthly') {
    return startD.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
  }
  const sameMonth = startD.getMonth() === endD.getMonth();
  if (sameMonth) {
    const month = startD.toLocaleDateString(locale, { month: 'short' });
    return `${month} ${startD.getDate()} – ${endD.getDate()}, ${endD.getFullYear()}`;
  }
  const startLabel = startD.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
  const endLabel = endD.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
  return `${startLabel} – ${endLabel}, ${endD.getFullYear()}`;
}
